import { ForwardedRef, forwardRef, Key, memo, ReactNode, useCallback, useRef, useState } from 'react'

import { IconButton, SxProps } from '@mui/material'
import { useAnimate, motion, AnimatePresence } from 'motion/react'

import { mergeRefs } from '@shared/utils/common'
import { popInOut } from '@shared/utils/support'

type AnimatedCounterButtonProps = {
  action: () => void
  children: (props: {
    key: Key
    component: typeof motion.svg
    variants: typeof popInOut
    initial: string
    animate: string
    exit: string
  }) => ReactNode
  sx?: SxProps
}

// Transfer props are provided to enable Tooltip e.g.
const AnimatedCounterButtonRaw = (
  { action, children, sx, ...transferProps }: AnimatedCounterButtonProps,
  ref: ForwardedRef<HTMLElement>,
) => {
  const [disabled, setDisabled] = useState(false)

  const z = useRef(0)
  const [scope, animate] = useAnimate()

  const handleRefreshClick = useCallback(async () => {
    action()

    z.current = z.current + 360

    setDisabled(true)

    await animate(scope.current, { rotateZ: z.current }, { duration: 0.8, delay: 0.1, type: 'spring' })

    setDisabled(false)
  }, [action, animate, scope])

  return (
    <IconButton
      {...transferProps}
      sx={[
        {
          display: 'grid',
          placeItems: 'center',
          font: 'unset',
          '&.Mui-disabled': { color: (theme) => `${theme.palette.grey[500]} !important` },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      ref={mergeRefs(scope, ref)}
      onClick={handleRefreshClick}
      disabled={disabled}
      component={motion.button}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <AnimatePresence mode="wait" initial={false}>
        {children({
          key: 'animated-icon-button',
          component: motion.svg,
          variants: popInOut,
          initial: 'hidden',
          animate: 'visible',
          exit: 'hidden',
        })}
      </AnimatePresence>
    </IconButton>
  )
}

export const AnimatedCounterButton = memo(forwardRef(AnimatedCounterButtonRaw))
